import React, { useState, useEffect, useCallback } from 'react';
import {
  Card,
  Table,
  Button,
  Space,
  Tag,
  Select,
  Switch,
  Modal,
  Form,
  Input,
  DatePicker,
  Popconfirm,
  Typography,
  Row,
  Col,
  Statistic,
  Alert,
  Spin,
  Tooltip,
  message,
} from 'antd';
import {
  PlusOutlined,
  EditOutlined,
  StopOutlined,
  ThunderboltFilled,
  ReloadOutlined,
  WarningFilled,
} from '@ant-design/icons';
import type { ColumnsType } from 'antd/es/table';
import dayjs from 'dayjs';
import {
  shutoffsApi,
  Shutoff,
  ShutoffStats,
  ShutoffType,
  ShutoffCreatePayload,
  ShutoffUpdatePayload,
} from '../../api/shutoffs';
import { DistrictSelect } from '../../components/shared';

const { Title, Text } = Typography;
const { TextArea } = Input;
const { Option } = Select;

const TYPE_CONFIG: Record<ShutoffType, { label: string; color: string }> = {
  water: { label: 'Вода', color: 'blue' },
  electricity: { label: 'Электричество', color: 'gold' },
  gas: { label: 'Газ', color: 'green' },
  heating: { label: 'Отопление', color: 'orange' },
};

const TYPE_OPTIONS: { value: ShutoffType; label: string }[] = [
  { value: 'water', label: 'Вода' },
  { value: 'electricity', label: 'Электричество' },
  { value: 'gas', label: 'Газ' },
  { value: 'heating', label: 'Отопление' },
];

function getStatusBadge(shutoff: Shutoff) {
  if (shutoff.is_cancelled) {
    return <Tag color="default">Отменено</Tag>;
  }
  if (shutoff.is_active) {
    return <Tag color="red">Идёт сейчас</Tag>;
  }
  if (dayjs(shutoff.start_time).isAfter(dayjs())) {
    return <Tag color="blue">Предстоит</Tag>;
  }
  return <Tag color="default">Завершено</Tag>;
}

interface FormValues {
  type: ShutoffType;
  reason: string;
  reason_kk?: string;
  affected_addresses?: string;
  district?: string;
  start_time: dayjs.Dayjs;
  end_time?: dayjs.Dayjs;
  is_emergency: boolean;
  send_push: boolean;
}

const ShutoffsList: React.FC = () => {
  const [shutoffs, setShutoffs] = useState<Shutoff[]>([]);
  const [stats, setStats] = useState<ShutoffStats | null>(null);
  const [total, setTotal] = useState(0);
  const [page, setPage] = useState(1);
  const [pageSize] = useState(20);

  const [loading, setLoading] = useState(false);
  const [statsLoading, setStatsLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const [filterType, setFilterType] = useState<ShutoffType | undefined>();
  const [filterDistrict, setFilterDistrict] = useState<string | undefined>();
  const [filterActiveOnly, setFilterActiveOnly] = useState(false);

  const [modalOpen, setModalOpen] = useState(false);
  const [editingShutoff, setEditingShutoff] = useState<Shutoff | null>(null);
  const [submitting, setSubmitting] = useState(false);

  const [form] = Form.useForm<FormValues>();
  const [messageApi, contextHolder] = message.useMessage();

  const fetchStats = useCallback(async () => {
    setStatsLoading(true);
    try {
      const res = await shutoffsApi.stats();
      setStats(res.data);
    } catch {
      // Stats failure is non-critical
    } finally {
      setStatsLoading(false);
    }
  }, []);

  const fetchShutoffs = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const res = await shutoffsApi.list({
        type: filterType,
        district: filterDistrict,
        active_only: filterActiveOnly || undefined,
        page,
        size: pageSize,
      });
      const data = res.data;
      if (Array.isArray(data)) {
        setShutoffs(data as unknown as Shutoff[]);
        setTotal((data as unknown as Shutoff[]).length);
      } else {
        setShutoffs(data.items);
        setTotal(data.total);
      }
    } catch (err: any) {
      setError(err?.response?.data?.detail || 'Ошибка загрузки данных');
    } finally {
      setLoading(false);
    }
  }, [filterType, filterDistrict, filterActiveOnly, page, pageSize]);

  useEffect(() => {
    fetchShutoffs();
  }, [fetchShutoffs]);

  useEffect(() => {
    fetchStats();
  }, [fetchStats]);

  const openCreate = () => {
    setEditingShutoff(null);
    form.resetFields();
    form.setFieldsValue({ is_emergency: false, send_push: true });
    setModalOpen(true);
  };

  const openEdit = (record: Shutoff) => {
    setEditingShutoff(record);
    form.setFieldsValue({
      type: record.type,
      reason: record.reason,
      reason_kk: record.reason_kk,
      affected_addresses: record.affected_addresses?.join(', '),
      district: record.district,
      start_time: dayjs(record.start_time),
      end_time: record.end_time ? dayjs(record.end_time) : undefined,
      is_emergency: record.is_emergency,
      send_push: false,
    });
    setModalOpen(true);
  };

  const handleSubmit = async () => {
    let values: FormValues;
    try {
      values = await form.validateFields();
    } catch {
      return;
    }

    setSubmitting(true);
    try {
      const addresses = values.affected_addresses
        ? values.affected_addresses.split(',').map((s) => s.trim()).filter(Boolean)
        : undefined;

      if (editingShutoff) {
        const payload: ShutoffUpdatePayload = {
          type: values.type,
          reason: values.reason,
          reason_kk: values.reason_kk || undefined,
          affected_addresses: addresses,
          district: values.district || undefined,
          start_time: values.start_time.toISOString(),
          end_time: values.end_time ? values.end_time.toISOString() : undefined,
          is_emergency: values.is_emergency,
        };
        await shutoffsApi.update(editingShutoff.id, payload);
        messageApi.success('Отключение обновлено');
      } else {
        const payload: ShutoffCreatePayload = {
          type: values.type,
          reason: values.reason,
          reason_kk: values.reason_kk || undefined,
          affected_addresses: addresses,
          district: values.district || undefined,
          start_time: values.start_time.toISOString(),
          end_time: values.end_time ? values.end_time.toISOString() : undefined,
          is_emergency: values.is_emergency,
          send_push: values.send_push,
        };
        await shutoffsApi.create(payload);
        messageApi.success('Отключение создано');
      }
      setModalOpen(false);
      fetchShutoffs();
      fetchStats();
    } catch (err: any) {
      messageApi.error(err?.response?.data?.detail || 'Ошибка при сохранении');
    } finally {
      setSubmitting(false);
    }
  };

  const handleCancel = async (id: number) => {
    try {
      await shutoffsApi.cancel(id);
      messageApi.success('Отключение отменено');
      fetchShutoffs();
      fetchStats();
    } catch (err: any) {
      messageApi.error(err?.response?.data?.detail || 'Ошибка при отмене');
    }
  };

  const columns: ColumnsType<Shutoff> = [
    {
      title: 'Тип',
      dataIndex: 'type',
      key: 'type',
      width: 130,
      render: (type: ShutoffType) => {
        const cfg = TYPE_CONFIG[type];
        return <Tag>{cfg?.label || type}</Tag>;
      },
    },
    {
      title: 'Причина',
      dataIndex: 'reason',
      key: 'reason',
      ellipsis: true,
      render: (reason: string, record) => (
        <Tooltip title={reason}>
          <Space direction="vertical" size={0}>
            <Text style={{ maxWidth: 240 }} ellipsis>{reason || '—'}</Text>
            {record.reason_kk && (
              <Text type="secondary" style={{ fontSize: 12 }} ellipsis>
                {record.reason_kk}
              </Text>
            )}
          </Space>
        </Tooltip>
      ),
    },
    {
      title: 'Район',
      dataIndex: 'district',
      key: 'district',
      width: 160,
      render: (d: string) => d || <Text type="secondary">—</Text>,
    },
    {
      title: 'Начало',
      dataIndex: 'start_time',
      key: 'start_time',
      width: 150,
      render: (t: string) => dayjs(t).format('DD.MM.YYYY HH:mm'),
    },
    {
      title: 'Конец',
      dataIndex: 'end_time',
      key: 'end_time',
      width: 150,
      render: (t?: string) =>
        t ? dayjs(t).format('DD.MM.YYYY HH:mm') : <Text type="secondary">Не указано</Text>,
    },
    {
      title: 'Статус',
      key: 'status',
      width: 130,
      render: (_: any, record: Shutoff) => getStatusBadge(record),
    },
    {
      title: '',
      key: 'emergency',
      width: 40,
      render: (_: any, record: Shutoff) =>
        record.is_emergency ? (
          <Tooltip title="Аварийное отключение">
            <WarningFilled style={{ color: 'var(--l-danger)', fontSize: 16 }} />
          </Tooltip>
        ) : null,
    },
    {
      title: 'Действия',
      key: 'actions',
      width: 120,
      render: (_: any, record: Shutoff) => (
        <Space size={4}>
          <Tooltip title="Редактировать">
            <Button
              size="small"
              icon={<EditOutlined />}
              onClick={() => openEdit(record)}
              disabled={record.is_cancelled}
            />
          </Tooltip>
          <Popconfirm
            title="Отменить отключение?"
            description="Это действие пометит отключение как отменённое."
            onConfirm={() => handleCancel(record.id)}
            okText="Да, отменить"
            cancelText="Нет"
            okButtonProps={{ danger: true }}
            disabled={record.is_cancelled}
          >
            <Tooltip title="Отменить">
              <Button
                size="small"
                icon={<StopOutlined />}
                danger
                disabled={record.is_cancelled}
              />
            </Tooltip>
          </Popconfirm>
        </Space>
      ),
    },
  ];

  return (
    <div>
      {contextHolder}

      {/* Header */}
      <div style={{ marginBottom: 24, display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start' }}>
        <div>
          <Title level={3} style={{ margin: 0, color: 'var(--l-ink)' }}>
            Отключения ЖКХ
          </Title>
          <Text type="secondary">Управление плановыми и аварийными отключениями коммунальных услуг</Text>
        </div>
        <Button
          type="primary"
          icon={<PlusOutlined />}
          onClick={openCreate}
         
          style={{ background: 'var(--l-accent)' }}
        >
          Создать отключение
        </Button>
      </div>

      {/* Stats Cards */}
      <Row gutter={16} style={{ marginBottom: 24 }}>
        <Col xs={24} sm={12} lg={6}>
          <Card>
            <Spin spinning={statsLoading} size="small">
              <Statistic
                title="Всего"
                value={stats?.total ?? 0}
                prefix={<ThunderboltFilled style={{ color: 'var(--l-muted)' }} />}
                valueStyle={{ color: 'var(--l-ink)' }}
              />
            </Spin>
          </Card>
        </Col>
        <Col xs={24} sm={12} lg={6}>
          <Card style={{ }}>
            <Spin spinning={statsLoading} size="small">
              <Statistic
                title="Активных"
                value={stats?.active ?? 0}
                valueStyle={{ color: 'var(--l-danger)' }}
              />
            </Spin>
          </Card>
        </Col>
        <Col xs={24} sm={12} lg={6}>
          <Card style={{ }}>
            <Spin spinning={statsLoading} size="small">
              <Statistic
                title="Предстоящих"
                value={stats?.upcoming ?? 0}
                valueStyle={{ color: 'var(--l-accent)' }}
              />
            </Spin>
          </Card>
        </Col>
        <Col xs={24} sm={12} lg={6}>
          <Card style={{ }}>
            <Spin spinning={statsLoading} size="small">
              <Statistic
                title="Аварийных"
                value={stats?.emergency_count ?? 0}
                valueStyle={{ color: '#ff7a45' }}
                prefix={<WarningFilled />}
              />
            </Spin>
          </Card>
        </Col>
      </Row>

      {/* Filters */}
      <Card style={{ marginBottom: 16 }} bodyStyle={{ padding: '12px 16px' }}>
        <Space wrap>
          <Select
            placeholder="Тип услуги"
            allowClear
            style={{ width: 180 }}
            value={filterType}
            onChange={(v) => { setFilterType(v); setPage(1); }}
          >
            {TYPE_OPTIONS.map((o) => (
              <Option key={o.value} value={o.value}>
                {o.label}
              </Option>
            ))}
          </Select>

          <DistrictSelect
            placeholder="Район"
            style={{ width: 200 }}
            value={filterDistrict}
            onChange={(v) => { setFilterDistrict(v as string | undefined); setPage(1); }}
          />

          <Space>
            <Switch
              checked={filterActiveOnly}
              onChange={(v) => { setFilterActiveOnly(v); setPage(1); }}
              size="small"
            />
            <Text>Только активные</Text>
          </Space>

          <Button icon={<ReloadOutlined />} onClick={() => { fetchShutoffs(); fetchStats(); }}>
            Обновить
          </Button>
        </Space>
      </Card>

      {/* Error */}
      {error && (
        <Alert
          type="error"
          message={error}
          closable
          onClose={() => setError(null)}
          style={{ marginBottom: 16 }}
        />
      )}

      {/* Table */}
      <Card bodyStyle={{ padding: 0 }}>
        <Table<Shutoff>
          rowKey="id"
          columns={columns}
          dataSource={shutoffs}
          loading={loading}
          pagination={{
            current: page,
            pageSize,
            total,
            onChange: (p) => setPage(p),
            showTotal: (t) => `Всего: ${t}`,
            showSizeChanger: false,
          }}
          locale={{ emptyText: 'Отключения не найдены' }}
          rowClassName={(record) =>
            record.is_emergency && !record.is_cancelled ? 'ant-table-row-emergency' : ''
          }
          scroll={{ x: 900 }}
        />
      </Card>

      {/* Create / Edit Modal */}
      <Modal
        title={editingShutoff ? 'Редактировать отключение' : 'Создать отключение'}
        open={modalOpen}
        onCancel={() => setModalOpen(false)}
        onOk={handleSubmit}
        confirmLoading={submitting}
        okText={editingShutoff ? 'Сохранить' : 'Создать'}
        cancelText="Отмена"
        width={640}
        destroyOnClose
      >
        <Form
          form={form}
          layout="vertical"
          style={{ marginTop: 16 }}
        >
          <Row gutter={16}>
            <Col span={12}>
              <Form.Item
                name="type"
                label="Тип услуги"
                rules={[{ required: true, message: 'Выберите тип услуги' }]}
              >
                <Select placeholder="Выберите тип">
                  {TYPE_OPTIONS.map((o) => (
                    <Option key={o.value} value={o.value}>
                      {o.label}
                    </Option>
                  ))}
                </Select>
              </Form.Item>
            </Col>
            <Col span={12}>
              <Form.Item name="district" label="Район">
                <DistrictSelect placeholder="Весь город" />
              </Form.Item>
            </Col>
          </Row>

          <Form.Item
            name="reason"
            label="Причина (рус.)"
            rules={[{ required: true, message: 'Введите причину отключения' }]}
          >
            <TextArea
              rows={3}
              placeholder="Опишите причину отключения на русском языке"
              showCount
              maxLength={500}
            />
          </Form.Item>

          <Form.Item name="reason_kk" label="Себебі (каз.)">
            <TextArea
              rows={2}
              placeholder="Себебін қазақ тілінде сипаттаңыз (міндетті емес)"
              showCount
              maxLength={500}
            />
          </Form.Item>

          <Form.Item
            name="affected_addresses"
            label="Адреса (через запятую)"
            extra="Необязательно. Пример: ул. Ленина 1, ул. Мира 5"
          >
            <Input placeholder="ул. Ленина 1, ул. Мира 5, пр. Абая 12" />
          </Form.Item>

          <Row gutter={16}>
            <Col span={12}>
              <Form.Item
                name="start_time"
                label="Начало отключения"
                rules={[{ required: true, message: 'Укажите время начала' }]}
              >
                <DatePicker
                  showTime
                  format="DD.MM.YYYY HH:mm"
                  style={{ width: '100%' }}
                  placeholder="Дата и время начала"
                />
              </Form.Item>
            </Col>
            <Col span={12}>
              <Form.Item name="end_time" label="Окончание (необязательно)">
                <DatePicker
                  showTime
                  format="DD.MM.YYYY HH:mm"
                  style={{ width: '100%' }}
                  placeholder="Дата и время окончания"
                />
              </Form.Item>
            </Col>
          </Row>

          <Row gutter={32}>
            <Col>
              <Form.Item
                name="is_emergency"
                label="Аварийное"
                valuePropName="checked"
              >
                <Switch
                  checkedChildren="Да"
                  unCheckedChildren="Нет"
                />
              </Form.Item>
            </Col>
            {!editingShutoff && (
              <Col>
                <Form.Item
                  name="send_push"
                  label="Push-уведомление"
                  valuePropName="checked"
                >
                  <Switch
                    checkedChildren="Отправить"
                    unCheckedChildren="Не отправлять"
                  />
                </Form.Item>
              </Col>
            )}
          </Row>
        </Form>
      </Modal>
    </div>
  );
};

export default ShutoffsList;
